import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import CESCardScanner from './components/CESCardScanner';
import Auth from './components/Auth';
import Landing from './components/Landing';
import Onboarding from './components/Onboarding';
import Profile from './components/Profile';
import LeadsList from './components/LeadsList';
import Dashboard from './components/Dashboard';
import Subscription from './components/Subscription';
import { getUserProfile } from './services/userService';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const checkProfile = async () => {
      if (user?.uid) {
        try {
          const profile = await getUserProfile(user.uid);
          // If profile doesn't exist or doesn't have name/email, show onboarding
          if (!profile || !profile.name || !profile.email) {
            setShowOnboarding(true);
          }
        } catch (error) {
          console.error('Error checking profile:', error);
          // Show onboarding if we can't check
          setShowOnboarding(true);
        }
      }
      setCheckingProfile(false);
    };

    if (user && !loading) {
      checkProfile();
    } else if (!user && !loading) {
      setCheckingProfile(false);
    }
  }, [user, loading]);

  if (loading || checkingProfile) {
    return (
      <div className="min-h-screen bg-white dark:bg-zinc-950 flex items-center justify-center transition-colors duration-300">
        <div className="text-zinc-600 dark:text-zinc-400">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (showOnboarding) {
    return <Onboarding />;
  }

  return children;
}

// Main App Routes
function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-zinc-950 flex items-center justify-center transition-colors duration-300">
        <div className="text-zinc-600 dark:text-zinc-400">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/auth" element={!user ? <Auth /> : <Navigate to="/" replace />} />
      <Route
        path="/"
        element={
          user ? (
            <ProtectedRoute>
              <CESCardScanner />
            </ProtectedRoute>
          ) : (
            <Landing />
          )
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <ProtectedRoute>
            <LeadsList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <Subscription />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
